/** @file ClipList: the puppet's clips by category (none: the import offer); click plays, right-click edits. */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { MouseEvent, ReactElement } from 'react';
import { useState } from 'react';
import type { EditorStore } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { clipMenu } from './clipMenu';
import { ClipListEmpty } from './ClipListEmpty';
import { ClipMetaForm } from './ClipMetaForm';

/** The inline editor open in the list. */
type Editing = { kind: 'rename' | 'meta'; clip: string } | null;
/** A menu opener for a clip (null = empty space) in a category. */
type OpenMenu = (name: string | null, category: string) => (e: MouseEvent) => void;

const confirmBox = (text: string): boolean => window.confirm(text);

/** Renames `from` to the typed name (if changed) and keeps the playhead on it. */
function rename(store: EditorStore, from: string, to: string): void {
  const s = store.getState();
  const name = to.trim();
  if (name === '' || name === from) return;
  void s.dispatch([{ name: 'clip.rename', input: { clip: from, name } }], `rename ${from} -> ${name}`);
  if (s.playhead.clip === from) s.setPlayhead({ clip: name });
}

/** An inline name field: Enter or blur renames. */
function RenameField({ name, onDone }: { name: string; onDone: (to: string) => void }): ReactElement {
  const [text, setText] = useState(name);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onDone(text); }}>
      <input aria-label="clip name" autoFocus value={text} style={{ width: '100%', font: 'inherit' }}
        onChange={(e) => { setText(e.currentTarget.value); }} onBlur={() => { onDone(text); }} />
    </form>
  );
}

/** One clip row: click puts it on the playhead at frame 0, right-click opens the clip menu. */
function ClipItem({ store, clip, selected, onMenu }: { store: EditorStore; clip: Clip; selected: boolean; onMenu: (e: MouseEvent) => void }): ReactElement {
  return (
    <div role="option" aria-selected={selected} data-clip={clip.name} onContextMenu={onMenu}
      onClick={() => { store.getState().setPlayhead({ clip: clip.name, frame: 0, playing: false }); }}
      style={{ padding: '2px 10px', cursor: 'default', background: selected ? 'var(--accent-bg, #3a3220)' : 'transparent', color: selected ? 'var(--accent, #f2b544)' : 'inherit' }}>
      {clip.name}{clip.mirrorHalf ? ' ⇄' : ''}
    </div>
  );
}

/** Clips by category, in first-appearance order. */
function groupsOf(clips: Record<string, Clip>): [string, Clip[]][] {
  const groups = new Map<string, Clip[]>();
  for (const c of Object.values(clips)) groups.set(c.category, [...(groups.get(c.category) ?? []), c]);
  return [...groups];
}

/** Groups by `category`; the playhead clip is highlighted; rename and meta edit inline. */
export function ClipList(): ReactElement {
  const store = useEditorStore();
  const clips = useEditor((s) => s.puppet.doc?.clips) ?? {};
  const current = useEditor((s) => s.playhead.clip);
  const [editing, setEditing] = useState<Editing>(null);
  const ui = { rename: (clip: string) => { setEditing({ kind: 'rename', clip }); }, meta: (clip: string) => { setEditing({ kind: 'meta', clip }); }, confirm: confirmBox };
  const menu: OpenMenu = (name, category) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    store.getState().openMenu(e.clientX, e.clientY, clipMenu(store, name, category, ui));
  };
  const renaming = editing?.kind === 'rename' ? editing.clip : null;
  const metaClip = editing?.kind === 'meta' ? clips[editing.clip] : undefined;
  return (
    <div role="listbox" aria-label="Clips" style={{ width: 150, flex: 'none', overflow: 'auto', borderRight: '1px solid var(--border, #2a2e38)', fontSize: 12 }}
      onContextMenu={menu(null, clips[current ?? '']?.category ?? 'misc')}>
      <ClipListEmpty />
      {groupsOf(clips).map(([category, list]) => (
        <div key={category}>
          <div style={{ padding: '4px 6px 2px', fontSize: 10, textTransform: 'uppercase', color: 'var(--muted, #8b919e)' }}>{category}</div>
          {list.map((c) => (renaming === c.name
            ? <RenameField key={c.name} name={c.name} onDone={(to) => { setEditing(null); rename(store, c.name, to); }} />
            : <ClipItem key={c.name} store={store} clip={c} selected={c.name === current} onMenu={menu(c.name, category)} />))}
        </div>
      ))}
      {metaClip !== undefined && (
        <ClipMetaForm clip={metaClip} onCancel={() => { setEditing(null); }}
          onSave={(fields) => { setEditing(null); void store.getState().dispatch([{ name: 'clip.setMeta', input: { clip: metaClip.name, fields } }], `meta ${metaClip.name}`); }} />
      )}
    </div>
  );
}
